import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import RichTextEditor from '../../components/admin/RichTextEditor';
import { postApi, mediaApi } from '../../services/api';
import { slugify } from '../../utils/slug';

const MAX_FILE_SIZE = 5 * 1024 * 1024;

export default function PostsPage() {
  const [posts, setPosts] = useState([]);
  const [pagination, setPagination] = useState({ current_page: 1, last_page: 1, total: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    title: '', slug: '', excerpt: '', body: '', image_url: '', status: 'draft',
  });
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);

  const fetchData = async (page = 1) => {
    setLoading(true);
    try {
      const res = await postApi.list({ per_page: 12, page, search });
      const d = res.data.data;
      setPosts(d?.data || d || []);
      setPagination({ current_page: d?.current_page || 1, last_page: d?.last_page || 1, total: d?.total || 0 });
    } catch (err) { console.error(err); } finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, [search]);

  const openCreate = () => {
    setEditing(null);
    setForm({ title: '', slug: '', excerpt: '', body: '', image_url: '', status: 'draft' });
    setShowModal(true);
  };

  const openEdit = (post) => {
    setEditing(post);
    setForm({ title: post.title, slug: post.slug, excerpt: post.excerpt || '', body: post.body || '', image_url: post.image_url || '', status: post.status });
    setShowModal(true);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) return alert('Hanya file gambar yang diizinkan');
    if (file.size > MAX_FILE_SIZE) return alert('Ukuran file maksimal 5MB');
    setUploadingImage(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await mediaApi.upload(fd);
      setForm(prev => ({ ...prev, image_url: res.data.data?.url || '' }));
    } catch (err) { alert('Gagal upload gambar.'); } finally { setUploadingImage(false); e.target.value = ''; }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      if (editing) {
        await postApi.update(editing.id, payload);
      } else {
        await postApi.create(payload);
      }
      setShowModal(false);
      fetchData(pagination.current_page);
    } catch (err) {
      alert(err.response?.data?.message || 'Error saving');
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Hapus artikel ini?')) return;
    try { await postApi.delete(id); fetchData(pagination.current_page); } catch (err) { alert('Gagal menghapus'); }
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' }) : '-';
  const pages = [];
  for (let i = Math.max(1, pagination.current_page - 2); i <= Math.min(pagination.last_page, pagination.current_page + 2); i++) pages.push(i);

  return (
    <AdminLayout>
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-lg font-semibold text-gray-800 mb-1">Blog & Artikel</h1>
          <button onClick={openCreate} className="px-4 py-2 bg-brand text-white rounded-lg hover:bg-brand-dark font-medium">
            + Tambah Artikel
          </button>
        </div>

        <div className="bg-white rounded-lg shadow overflow-hidden mb-4">
          <div className="p-4 border-b border-gray-100">
            <input type="text" placeholder="Cari artikel..." value={search} onChange={e => setSearch(e.target.value)} className="w-full max-w-sm px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-brand outline-none" />
          </div>

          {loading ? (
            <div className="flex justify-center py-12"><div className="w-8 h-8 border-4 border-brand border-t-transparent rounded-full animate-spin" /></div>
          ) : posts.length === 0 ? (
            <div className="text-center py-12 text-gray-400">Belum ada artikel.</div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Gambar</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Judul</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Slug</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tanggal</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Aksi</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {posts.map((post) => (
                  <tr key={post.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      {post.image_url ? <img src={post.image_url} alt="" className="w-12 h-12 rounded object-cover" /> : <span className="text-2xl">📝</span>}
                    </td>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900 max-w-xs truncate">{post.title}</td>
                    <td className="px-4 py-3 text-sm text-gray-500 font-mono text-xs">{post.slug}</td>
                    <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">{formatDate(post.published_at || post.created_at)}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 text-xs rounded-full font-medium ${post.status === 'published' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                        {post.status === 'published' ? 'Published' : 'Draft'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right space-x-2">
                      <button onClick={() => openEdit(post)} className="text-blue-600 hover:text-blue-800 text-sm">Edit</button>
                      <button onClick={() => handleDelete(post.id)} className="text-red-600 hover:text-red-800 text-sm">Hapus</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {pagination.last_page > 1 && (
          <div className="flex items-center justify-between bg-white rounded-lg shadow px-4 py-3">
            <span className="text-sm text-gray-500">Total {pagination.total} artikel · Halaman {pagination.current_page} dari {pagination.last_page}</span>
            <div className="flex items-center gap-1">
              <button disabled={pagination.current_page === 1} onClick={() => fetchData(pagination.current_page - 1)} className="px-3 py-1 text-sm rounded border border-gray-300 disabled:opacity-30 hover:bg-gray-50">« Prev</button>
              {pages.map(p => (
                <button key={p} onClick={() => fetchData(p)} className={`px-3 py-1 text-sm rounded ${p === pagination.current_page ? 'bg-brand text-white' : 'border border-gray-300 hover:bg-gray-50'}`}>{p}</button>
              ))}
              <button disabled={pagination.current_page === pagination.last_page} onClick={() => fetchData(pagination.current_page + 1)} className="px-3 py-1 text-sm rounded border border-gray-300 disabled:opacity-30 hover:bg-gray-50">Next »</button>
            </div>
          </div>
        )}

        {/* MODAL - FULL SCREEN with SCROLL */}
        {showModal && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center p-4 overflow-y-auto">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl my-8">
              {/* Fixed header */}
              <div className="sticky top-0 bg-white rounded-t-2xl border-b border-gray-100 px-6 py-4 flex items-center justify-between z-10">
                <h2 className="text-lg font-bold text-gray-900">{editing ? 'Edit Artikel' : 'Tambah Artikel'}</h2>
                <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
              </div>

              {/* Scrollable body */}
              <form onSubmit={handleSave} className="p-6 space-y-5 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 200px)' }}>
                <div className="grid grid-cols-3 gap-4">
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Judul *</label>
                    <input required value={form.title} onChange={e => { const v = e.target.value; setForm({ ...form, title: v, slug: slugify(v) }); }} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Slug (tanpa spasi) *</label>
                    <input required value={form.slug} onChange={e => setForm({ ...form, slug: slugify(e.target.value) })} pattern="^[a-z0-9]+(?:-[a-z0-9]+)*$" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono" />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Excerpt</label>
                  <textarea value={form.excerpt} onChange={e => setForm({ ...form, excerpt: e.target.value })} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" rows={2} placeholder="Ringkasan singkat artikel..." />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Foto Artikel</label>
                  <div className="flex items-start gap-4">
                    <div>
                      <label className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium cursor-pointer transition ${uploadingImage ? 'bg-gray-300 text-gray-500' : 'bg-blue-500 text-white hover:bg-blue-600'}`}>
                        {uploadingImage ? 'Mengupload...' : form.image_url ? '📸 Ganti Foto' : '📤 Upload Foto'}
                        <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" disabled={uploadingImage} />
                      </label>
                      <p className="text-xs text-gray-400 mt-1">Max 5MB. Gambar disimpan di server.</p>
                    </div>
                    {form.image_url && (
                      <div className="relative">
                        <img src={form.image_url} alt="Preview" className="w-40 h-24 object-cover rounded-lg border" />
                        <button type="button" onClick={() => setForm(prev => ({ ...prev, image_url: '' }))} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs hover:bg-red-600">✕</button>
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <RichTextEditor
                    label="Konten Artikel (HTML)"
                    value={form.body}
                    onChange={v => setForm({ ...form, body: v })}
                    height={550}
                    placeholder="Tulis konten artikel lengkap..."
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                    <select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                      <option value="draft">Draft</option>
                      <option value="published">Published</option>
                    </select>
                  </div>
                </div>

                {/* Fixed footer */}
                <div className="sticky bottom-0 bg-white pt-4 border-t border-gray-100 flex justify-end gap-3">
                  <button type="button" onClick={() => setShowModal(false)} className="px-6 py-2.5 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50">Batal</button>
                  <button type="submit" disabled={saving} className="px-6 py-2.5 bg-brand text-white rounded-lg font-medium hover:bg-brand-dark disabled:opacity-50">
                    {saving ? 'Menyimpan...' : editing ? 'Update Artikel' : 'Publikasi Artikel'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}